import { KatInput } from '@amzn/katal-react';
import { FC, useState, useCallback, useRef, useEffect } from 'react';
import { search } from 'src/utils/clients/controlPlane/apiMethods';
import { getControlPlaneClient } from 'src/contexts/ClientContext';
import { useSelection } from 'src/contexts/SelectionContext';
import {
  SearchInput,
  SearchProvider,
  SearchResult,
} from '@amzn/fire-tv-campaign-automation-service';
import styles from './SearchImageInput.module.css';
import { ImageSelectionModal } from 'src/components/general/ImageSelectionModal';
import { StateConstants } from 'src/constants';
import { SearchBar } from './SearchBar';
import { SelectedImagePreview } from './SelectedImagePreview';
import { SearchState } from './SearchState';
import { SearchResults } from './SearchResults';
import { logger } from 'src/logger';

export interface SearchImageInputProps {
  label?: string;
  placeholder?: string;
  maxLength?: number;
  onImageSelect: (imageUrl: string, imageTitle?: string) => void;
  selectedImageUrl?: string | null;
  selectedImageTitle?: string | null;
  onClearSelection?: () => void;
  disabled?: boolean;
}

export const SearchImageInput: FC<SearchImageInputProps> = ({
  label = 'Search',
  placeholder = 'Search for images...',
  maxLength,
  onImageSelect,
  selectedImageUrl,
  selectedImageTitle,
  onClearSelection,
  disabled = false,
}) => {
  const [query, setQuery] = useState('');
  const queryRef = useRef(query);
  const [searchResults, setSearchResults] = useState<SearchResult[] | undefined>(undefined);
  const [showImageModal, setShowImageModal] = useState(false);
  const [title, setTitle] = useState(StateConstants.EMPTY_STRING);
  const [isSearching, setIsSearching] = useState(false);
  const [selectedResult, setSelectedResult] = useState<SearchResult | undefined>(undefined);
  const [searchError, setSearchError] = useState<string | undefined>(undefined);

  const { marketplace, locale } = useSelection();
  const controlPlaneClient = getControlPlaneClient();

  // Clear search results when locale changes
  const prevLocaleRef = useRef(locale);
  useEffect(() => {
    if (prevLocaleRef.current !== undefined && prevLocaleRef.current !== locale) {
      setSearchResults(undefined);
      setQuery('');
      setSelectedResult(undefined);
      setSearchError(undefined);
    }
    prevLocaleRef.current = locale;
  }, [locale]);

  // Clear selected image when props are cleared (e.g., by locale change reset)
  const prevSelectedImageUrlRef = useRef(selectedImageUrl);
  useEffect(() => {
    if (prevSelectedImageUrlRef.current && !selectedImageUrl) {
      // Image was cleared externally (e.g., locale change), call onClearSelection
      // to sync the parent's local state (selectedImageTitle)
      onClearSelection?.();
    }
    prevSelectedImageUrlRef.current = selectedImageUrl;
  }, [selectedImageUrl, onClearSelection]);

  const sanitizeQuery = (input: string): string => {
    return input.replace(/[<>"'&]/g, '');
  };

  const handleSearch = useCallback(
    async (searchQuery: string) => {
      if (!searchQuery.trim() || !marketplace || !locale) return;

      setIsSearching(true);
      setSearchError(undefined);
      queryRef.current = searchQuery.trim();

      try {
        const searchInput: SearchInput = {
          query: sanitizeQuery(searchQuery.trim()),
          marketplace,
          locale,
          isCatalogueId: false,
          searchProvider: SearchProvider.KTQR,
        };

        const response = await search(controlPlaneClient, searchInput);
        const validResults = response.results.filter(
          (result) => result.images && result.images.some((img) => img.url && img.url.trim()),
        );
        setSearchResults(validResults);
      } catch (error) {
        setSearchResults([]);
        logger.debug('Error searching images:', { error });
        setSearchError(
          error instanceof Error
            ? error.message
            : `Failed to search images for ${searchQuery.trim()}`,
        );
      } finally {
        setIsSearching(false);
      }
    },
    [marketplace, locale, controlPlaneClient],
  );

  const handleInputChange = (event: KatInput.ChangeEvent) => {
    setQuery(event.currentTarget.value);
  };

  const handleKeyPress = (event: KatInput.KeydownEvent) => {
    if ('key' in event && event.key === 'Enter') {
      handleSearch(query);
    }
  };

  const handleSearchClick = () => {
    handleSearch(query);
  };

  const handleClearSearch = () => {
    setQuery('');
    queryRef.current = '';
    setSearchResults([]);
    setSearchError(undefined);
  };

  const handleTileClick = (result: SearchResult) => {
    setSelectedResult(result);
    setTitle(result.title);
    setShowImageModal(true);
  };

  const handleImageSelection = (imageUrl: string) => {
    if (selectedResult) {
      onImageSelect(imageUrl, selectedResult.title);
    }
    setShowImageModal(false);
    setSelectedResult(undefined);
  };

  const handleClearSelection = () => {
    onClearSelection?.();
  };

  const handleCloseModal = () => {
    setShowImageModal(false);
    setSelectedResult(undefined);
  };

  const imageUrls = selectedResult?.images.map((image) => image.url) ?? [];

  return (
    <div className={styles.container}>
      <SearchBar
        label={label}
        placeholder={placeholder}
        query={query}
        isSearching={isSearching}
        disabled={disabled}
        maxLength={maxLength}
        onInputChange={handleInputChange}
        onKeyDown={handleKeyPress}
        onSearchClick={handleSearchClick}
        onClearSearch={handleClearSearch}
      />

      <SearchState
        isSearching={isSearching}
        searchError={searchError}
        searchResults={searchResults}
        queryRef={queryRef}
      />

      {searchResults && searchResults?.length > 0 && (
        <SearchResults
          results={searchResults}
          onTileClick={handleTileClick}
          disabled={disabled}
          selectedImageUrl={selectedImageUrl}
        />
      )}

      {selectedImageUrl && selectedImageTitle && (
        <SelectedImagePreview
          imageUrl={selectedImageUrl}
          imageTitle={selectedImageTitle}
          disabled={disabled}
          onClearSelection={handleClearSelection}
        />
      )}

      <ImageSelectionModal
        isVisible={showImageModal}
        imageUrls={imageUrls}
        onClose={handleCloseModal}
        onSelect={handleImageSelection}
        title={title}
      />
    </div>
  );
};
